import { MAX_ROUNDS, Role } from "@gigsync/protocol";
/**
 * Hidden settings for one job's negotiation (ADR 0033 §1). When a side's agent first plans a job, its phone draws them
 * from the platform's cryptographic random source; they are stored with the job and never leave the phone. A fixed
 * curve let the other side work the limit out from the first offer or two; with these, the same limits settle at
 * different prices and the offers only narrow the limit to a range. Code still keeps every bound: no offer passes the
 * owner's limit (job/negotiate.ts).
 *
 * The curve is not a power curve with jitter: each offer concedes a share of the gap still left to the limit, drawn
 * on its own, and the last offer before the horizon jumps the rest of the way. In simulation a power curve with ±40%
 * jitter let a counterpart guess the limit within 4% from the steps in about half the jobs, because the steps give
 * the exponent away; independent shares do not (test/negotiate-leak.test.ts).
 */
/** How the owner wants their agent to bargain; a model picks one from the owner's own words (job/tactic.ts). */
export const Tactic = { patient: "patient", steady: "steady", quick: "quick" };
/** In the order Settings shows them. */
export const TACTICS = [Tactic.patient, Tactic.steady, Tactic.quick];
/** Without a model, an owner's choice or a clear answer, the agent bargains steadily. */
export const DEFAULT_TACTIC = Tactic.steady;
/**
 * Patient opens further away, gives up less each time, comes to its limit late and haggles closer; quick the reverse.
 * Tuned with the leak test: each range is wide enough that a counterpart who knows the tactic still cannot fit the
 * limit from the offers it sees.
 */
export const TACTIC_RANGES = {
    [Tactic.patient]: {
        share: [0.35, 0.65],
        position: [0.6, 1],
        concede: [0.06, 0.3],
        horizon: [4, 5],
        closeness: [0.02, 0.035],
    },
    [Tactic.steady]: {
        share: [0.4, 0.9],
        position: [0.3, 1],
        concede: [0.1, 0.36],
        horizon: [3, 5],
        closeness: [0.03, 0.05],
    },
    [Tactic.quick]: {
        share: [0.5, 0.9],
        position: [0, 0.7],
        concede: [0.12, 0.38],
        horizon: [3, 4],
        closeness: [0.04, 0.06],
    },
};
/** Each agent's own offers before its owner takes over (ADR 0053 §4); the protocol allows `MAX_ROUNDS` a side. */
export const AGENT_OFFERS = 3;
/** A number drawn evenly from the range. */
const draw = (random, [low, high]) => low + (high - low) * random.next();
/** A whole number drawn evenly from the range, ends included; a source that returns 1 still lands inside. */
const drawWhole = (random, [low, high]) => Math.min(high, low + Math.floor(random.next() * (high - low + 1)));
/** Draws a job's hidden settings for this side and tactic. */
export function drawStrategy(role, tactic, random) {
    const ranges = TACTIC_RANGES[tactic];
    const opening = draw(random, role === Role.enum.provider ? ranges.position : ranges.share);
    // One share per step between offers; the last offer is the limit itself, so there are at most MAX_ROUNDS - 2.
    const concede = Array.from({ length: MAX_ROUNDS - 2 }, () => draw(random, ranges.concede));
    return {
        tactic,
        opening,
        concede,
        horizon: drawWhole(random, ranges.horizon),
        closeness: draw(random, ranges.closeness),
    };
}
/**
 * How far this side has come from its opening toward its limit by its `round`-th offer: 0 at the opening, 1 from the
 * horizon on, and never less than the offer before.
 */
export function conceded(s, round) {
    // An agent offers its limit by its last offer before its owner takes over (ADR 0053 §4).
    if (round >= Math.min(s.horizon, AGENT_OFFERS))
        return 1;
    let left = 1;
    for (let step = 0; step < round - 1; step++)
        left *= 1 - Math.min(1, Math.max(0, s.concede[step] ?? 0));
    return 1 - left;
}
//# sourceMappingURL=strategy.js.map